(ns nosco.i18n.translate
  "Functions to query translations and to translate text on the client side using a \"translation context\".
  The translation context is made of:
  - A map of text keys to singular-form or plural-form translations
  - A function that, given a number, returns the index of the plural-form translation"
  (:require [clojure.string :as string]
            [goog.object :as gobj]
            [nosco.ui.html :as html]
            [nosco.i18n.plural :as plural]
            [nosco.controllers :as controllers]
            [nosco.i18n.translate-utils :as t-utils])
  (:require-macros [nosco.i18n.translate :as t]
                   [nosco.conf.locales :as locales]))


(defn handle-translations-result
  "Parse the translation-context and add the \"plural-fn\" associated with the locale of the user."
  [translation-context]
  (let [translation-context (.parse js/JSON translation-context)
        locale (gobj/get translation-context "locale")
        plural-fn (:fn (get plural/plural-rules locale))]
    (gobj/set translation-context "plural-fn" plural-fn)
    translation-context))

;; Init the translation context. We could make a separate HTTP request
;; for this, but there's no real need, since we can easily embed the
;; whole translation context in the HTML a JSON var.

(defonce ^:export -translation-context
  (handle-translations-result (or (gobj/get js/window "translations_data_raw") "{}")))

(defn active-translation-context []
  -translation-context)

(defn ^:export active-locale
  "Return the active locale, or nil if translations data is not available (e.g. in tests)"
  []
  (some-> (active-translation-context)
          (.-locale)))

(defn escape-html [s]
  (when s
    (let [text (js/document.createTextNode s)
          el (js/document.createElement "span")]
      (.appendChild el text)
      (.-innerHTML el))))

(defn- get-translated-text [text]
  (let [translated-text (some-> (active-translation-context)
                                (gobj/get "translations")
                                (gobj/get text))
        translated-text (when translated-text
                          (gobj/get translated-text "translation-singular"))]
    (or translated-text text)))

(defn format-translation-text
  "Given a string and a seq of vars, uses a translation context to compute the singular form of a translated string.
  The provided string may contain \"%s\" interpolation forms that are expanded using the provided vars.
  The number of interpolation forms must be equal to the number of vars."
  [text vars]
  (let [translated-text (get-translated-text text)
        s (t-utils/format-translation-text-impl translated-text vars)]
    s))

(defn format-translation-text-html
  "Same as \"format-translation-text\" but:
  - Accept HTML tags in translation text
  - Returns a Hiccup form with the HTML content set to the formatted translation text.
  - Escapes HTML special characters from params."
  [text vars]
  (let [translated-text (get-translated-text text)
        vars (map escape-html vars)
        s (t-utils/format-translation-text-impl translated-text vars)]
    [html/SafeHTMLStrict {:html s :tag :span}]))

(defn- get-translated-text-plural [plural-texts plural-n]
  (let [singular-text (first plural-texts)
        plural-fn (gobj/get (active-translation-context) "plural-fn")
        n (plural-fn plural-n)
        translated-text (-> (active-translation-context)
                            (gobj/get "translations")
                            (gobj/get singular-text))
        translated-text (when translated-text
                          (-> translated-text
                              (gobj/get "translation-plural")
                              (get n)))]
    (or translated-text
        (get plural-texts (plural/one-other-plural-fn plural-n)))))

(defn format-translation-text-plural
  "Given a pair of singular-string and plural-string, a number and a seq of vars, uses a translation context to compute the plural form of a translated string.
  The provided string may contain \"%s\" interpolation forms that are expanded using the provided vars.
  The nuumber of interpolation forms must be equal to the number of vars."
  [plural-texts plural-n vars]
  (let [translated-text (get-translated-text-plural plural-texts plural-n)
        s (t-utils/format-translation-text-impl translated-text vars)]
    s))

(defn format-translation-text-plural-html
  "Same as \"format-translation-text-plural\" but:
  - Accept HTML tags in translation text
  - Returns a Hiccup form with the HTML content set to the formatted translation text.
  - Escapes HTML special characters from params."
  [plural-texts plural-n vars]
  (let [translated-text (get-translated-text-plural plural-texts plural-n)
        vars (map escape-html vars)
        s (t-utils/format-translation-text-impl translated-text vars)]
    [html/SafeHTMLStrict {:html s :tag :span}]))

(comment
  (t-utils/format-translation-text-impl "%see %s ff%sgg%s" ["0" "1" "2" "3"])

  (t-utils/format-translation-text-impl "ddd" [])
  )
